import cn from 'classnames';
import { useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type { FullHeadMPRProjection } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useMedia } from '@/shared/hooks';
import { Checkbox } from '@/shared/ui';

import {
  MPRToolsBar,
  useAddFullHeadMprViewToReport,
  useMPRCommands,
  useMPRShortcuts,
  type FullHeadMprReportSelectionResolver,
} from '@/features/MPR';

import {
  FULL_HEAD_MPR_SESSION_PROJECTIONS,
  FULL_HEAD_MPR_TOOLS,
  type FullHeadMprSliceGroupView,
  type FullHeadMprSliceRecordViewsByTileID,
  type FullHeadMprSliceThicknessControl,
  type FullHeadMprSliceTileView,
  type ToothSliceGeometry,
  type ToothSliceOptionsBySeries,
} from '../../config';
import {
  useFullHeadMPRTools,
  useFullHeadMprEditor,
  useFullHeadMprSliceSession,
  useFullHeadMprSliceThicknessOverride,
  useToothSliceMode,
} from '../../hooks';
import {
  resolveFullHeadMprCursorIcon,
  resolveFullHeadMprSeriesTile,
} from '../../lib';
import viewerStyles from '../FullHeadMPR.module.scss';
import { FullHeadMPRPaneControls } from '../FullHeadMPRPaneControls';
import { FullHeadMprSliceSessionPane } from '../FullHeadMprSliceSessionPane';
import { FullHeadMPRStatus } from '../FullHeadMPRStatus';
import { FullHeadMprToolCursorBadge } from '../FullHeadMprToolCursorBadge';

import styles from './FullHeadMprSliceSession.module.scss';

type FullHeadMprSliceSessionProps = {
  group: FullHeadMprSliceGroupView | null;
  input: ToothSliceGeometry | null;
  recordsByTileID: FullHeadMprSliceRecordViewsByTileID;
  sliceOptions: ToothSliceOptionsBySeries;
  tile: FullHeadMprSliceTileView;
  toothID: string;
  className?: string;
  groups?: FullHeadMprSliceGroupView[];
  isSingleProjection?: boolean;
  isThicknessOverrideEnabled?: boolean;
  renderLayout: (parts: {
    children: ReactNode;
    contentProps: ReturnType<typeof useMPRShortcuts>;
    sliceThickness: FullHeadMprSliceThicknessControl;
    actions?: ReactNode;
    controls?: ReactNode;
    order?: number;
    shortcutsControl?: ReactNode;
    renderToolbar?: (className: string) => ReactNode;
  }) => ReactNode;
  onPrimaryProjectionChange: (projection: FullHeadMPRProjection | null) => void;
  resolveViewSelection?: FullHeadMprReportSelectionResolver;
};

export const FullHeadMprSliceSession: FC<FullHeadMprSliceSessionProps> = (
  props,
) => {
  const {
    recordsByTileID,
    className,
    isSingleProjection = false,
    isThicknessOverrideEnabled = false,
    renderLayout,
    onPrimaryProjectionChange,
    resolveViewSelection,
    ...params
  } = props;
  const { isMobile } = useMedia();
  const isImageToolbar = isSingleProjection && !isMobile;
  const containerRef = useRef<HTMLDivElement>(null);
  const [primaryProjection, setPrimaryProjection] =
    useState<FullHeadMPRProjection | null>(null);
  const seriesProjection =
    params.tile.seriesName === 'frontal' ? 'coronal' : params.tile.seriesName;
  const sourceProjection =
    params.tile.kind === 'view'
      ? params.tile.view.projection
      : seriesProjection;
  const visibleProjections = isSingleProjection
    ? [sourceProjection]
    : FULL_HEAD_MPR_SESSION_PROJECTIONS;
  const [sessionForOverride, setSessionForOverride] =
    useState<ReturnType<typeof useFullHeadMprSliceSession>['session']>(null);
  const seriesThicknessMm =
    params.tile.kind === 'series'
      ? {
          axial: params.sliceOptions.axial.thicknessMm,
          coronal: params.sliceOptions.frontal.thicknessMm,
          sagittal: params.sliceOptions.sagittal.thicknessMm,
        }
      : {};
  const isThicknessOverrideActive =
    isThicknessOverrideEnabled && params.tile.kind === 'series';
  const sliceThickness = useFullHeadMprSliceThicknessOverride({
    isEnabled: isThicknessOverrideActive,
    projection: primaryProjection,
    seriesThicknessMm,
    session: sessionForOverride,
    toothID: params.toothID,
  });
  const state = useFullHeadMprSliceSession({
    ...params,
    containerRef,
    record: recordsByTileID[params.tile.id],
    thicknessOverrideMm:
      sliceThickness.overrideMm !== null && primaryProjection
        ? { [primaryProjection]: sliceThickness.overrideMm }
        : undefined,
    visibleProjections,
  });
  const { session } = state;
  const activeProjection = state.activePane?.projection;

  useEffect(() => {
    setSessionForOverride(session);
    if (!session) {
      setPrimaryProjection(null);
      onPrimaryProjectionChange(null);
      return;
    }

    const update = () => {
      const projection = session.getPrimaryProjection();
      setPrimaryProjection(projection);
      onPrimaryProjectionChange(projection);
    };

    update();

    return session.addEventListener('fullHeadMPRPaneLayoutChanged', update);
  }, [session, onPrimaryProjectionChange]);

  const { setGeometryMode, toothMode } = useToothSliceMode({
    defaultGeometryMode: 'series',
    isReady: state.isReady,
    scope: 'modal',
    session,
  });

  const editor = useFullHeadMprEditor({
    isAnnotationsLocked: isThicknessOverrideActive,
    isReady: state.isReady,
    original: state.original,
    session,
    toothID: params.toothID,
  });
  const tools = useFullHeadMPRTools({
    canEditAnnotations: editor.canEditAnnotations,
    canEditImageOptions: editor.canEditImageOptions,
    isReady: state.isReady,
    onReset: state.reset,
    session,
  });
  // One pane has nothing to mirror onto, so the sync switch stays hidden.
  const isImageOptionsSynced = isSingleProjection
    ? undefined
    : tools.isImageOptionsSynced;
  const commands = useMPRCommands({
    ...tools,
    isImageOptionsSynced,
    screen: 'toothPage',
    tools: FULL_HEAD_MPR_TOOLS,
    toothID: params.toothID,
    isDisabled: !state.isReady,
    isDownloadDisabled: editor.isDownloadDisabled,
    onAxesVisibleChange: tools.setAxesVisible,
    onDownload: editor.download,
    onImageOptionsSyncedChange: tools.setImageOptionsSynced,
    onToolClick: tools.handleToolClick,
    onZoomSyncedChange: tools.setZoomSynced,
    onStepSlice: session
      ? (direction) => session.stepSlice(direction)
      : undefined,
  });
  const cursorIcon = resolveFullHeadMprCursorIcon({
    activeMode: tools.activeMode,
    annotationTool: tools.annotations.activeTool,
    imageTool: tools.imageOptions.activeTool,
  });
  const shortcuts = useMPRShortcuts({
    commands,
    isEnabled: state.isReady && !editor.isCheckingSignature,
  });
  const snapshot = activeProjection ? editor.snapshots[activeProjection] : null;
  const currentTile =
    isSingleProjection && snapshot
      ? resolveFullHeadMprSeriesTile({
          groups: params.groups ?? (params.group ? [params.group] : []),
          input: params.input,
          snapshot,
        })
      : null;
  const add = useAddFullHeadMprViewToReport({
    toothID: params.toothID,
    volumeFileID: state.original?.view.volumeFileID ?? null,
    original: state.original,
    getSnapshot: state.getSnapshot,
    resolveSelection: resolveViewSelection,
    snapshot,
  });
  return (
    <MPRToolsBar
      {...tools}
      isImageOptionsSynced={isImageOptionsSynced}
      screen="toothPage"
      tools={FULL_HEAD_MPR_TOOLS}
      commands={commands}
      shortcutsEnabled
      toothID={params.toothID}
      variant={isImageToolbar ? 'medicalImage' : 'default'}
      isDisabled={!state.isReady}
      paneLayout={!isSingleProjection && session ? session : undefined}
      standardPlanesMode={{
        isActive: toothMode.geometryMode === 'orthogonal',
        isAvailable: toothMode.isActive,
        onChange: (isStandardPlanesEnabled) =>
          setGeometryMode(isStandardPlanesEnabled ? 'orthogonal' : 'series'),
      }}
      onToolClick={tools.handleToolClick}
      onAxesVisibleChange={tools.setAxesVisible}
      onImageOptionsSyncedChange={tools.setImageOptionsSynced}
      onZoomSyncedChange={tools.setZoomSynced}
      renderLayout={({
        annotationsControl,
        controls,
        shortcutsControl,
        renderToolbar,
      }) => {
        const renderSessionToolbar = (toolbarClassName?: string) =>
          renderToolbar({
            className: toolbarClassName,
          });

        return renderLayout({
          contentProps: shortcuts,
          controls,
          sliceThickness,
          order: currentTile ? currentTile.order + 1 : undefined,
          shortcutsControl,
          renderToolbar: isImageToolbar ? renderSessionToolbar : undefined,
          actions: isSingleProjection && (
            <>
              <Checkbox
                checked={add.isSelected}
                disabled={
                  add.isLoading || add.isDisabled || !state.isActiveViewReady
                }
                onChange={add.onToggleSelection}
              >
                <FormattedMessage
                  id="medicalImages.Selected"
                  defaultMessage="Selected"
                />
              </Checkbox>
              {activeProjection &&
                editor.hasAnnotationsByProjection[activeProjection] &&
                annotationsControl}
            </>
          ),
          children: (
            <div
              className={className}
              data-component="full-head-mpr-slice-session"
            >
              {!isImageToolbar && renderSessionToolbar()}
              <div className={styles.container}>
                <div
                  ref={containerRef}
                  data-mpr-viewport
                  className={cn(
                    styles.viewer,
                    !state.isReady && viewerStyles.preparing,
                  )}
                />
                <FullHeadMPRStatus
                  errorMessage={state.errorMessage}
                  isReady={state.isReady}
                  onRetry={state.retry}
                />
                <FullHeadMprToolCursorBadge
                  containerRef={containerRef}
                  icon={state.isReady ? cursorIcon : null}
                />
              </div>
              <FullHeadMPRPaneControls
                isReady={Boolean(session)}
                session={session}
              />
              {editor.panes.map((pane) => (
                <FullHeadMprSliceSessionPane
                  key={pane.projection}
                  pane={pane}
                  addToReport={
                    state.isReady
                      ? add.getAddToReportProps({
                          isViewReady: pane.isReady,
                          snapshot: pane.snapshot,
                        })
                      : undefined
                  }
                  isAnnotationsHidden={!tools.annotations.isVisible}
                />
              ))}
            </div>
          ),
        });
      }}
    />
  );
};
